import api from '../lib/api';

export interface Hotel {
  id: number;
  extranet_hotel_id: string;
  hotel_name: string;
  hotel_type: 'Hotel' | 'Apartment' | 'Resort' | 'Hostel' | 'Guesthouse' | 'Villa';
  chain_brand?: string;
  star_rating?: number | null;
  street_address: string;
  landmark?: string | null;
  area?: string;
  city: string;
  state: string;
  zip_code?: string;
  country: string;
  destination: string;
  reservation_contact_number?: string | null;
  reservation_email?: string;
  support_email?: string;
  phone?: string;
  hotel_website?: string;
  checkin_time?: string | null;
  checkout_time?: string | null;
  hotel_description?: string;
  terms_conditions?: string;
  latitude?: number | null;
  longitude?: number | null;
  timezone?: string;
  vervotech_id?: string;
  contact_person_name?: string | null;
  designation?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  tax_details?: string | null;
  bank_account_details?: string | null;
  accepted_payment_methods?: string | null;
  currency?: string;
  markup_percentage?: number | null;
  service_charge_percentage?: number | null;
  cancellation_policy?: string;
  cancellation_policy_text?: string;
  cancellation_days?: number;
  cancellation_percentage?: number;
  no_show_policy?: string;
  no_show_policy_text?: string;
  no_show_percentage?: number;
  child_policy?: string;
  child_rate?: number;
  extra_adult_rate?: number;
  extra_bed_rate?: number;
  min_stay?: number | null;
  max_stay?: number | null;
  max_advance_booking_days?: number;
  special_offer_name?: string;
  special_offer_type?: 'Percentage Discount' | 'Free Night' | 'Value Add';
  special_offer_details?: string;
  offer_valid_from?: string | null;
  offer_valid_to?: string | null;
  applicable_market?: string;
  status: 'active' | 'inactive' | 'suspended';
  remarks?: string;
  special_conditions?: string;
  created_at: string;
  updated_at: string;
}

export interface HotelFilters {
  status?: string;
  city?: string;
  country?: string;
  hotel_type?: Hotel['hotel_type'];
  extranet_hotel_id?: string;
  star_rating?: number;
  search?: string;
}

export interface HotelResponse {
  success: boolean;
  data: Hotel[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  timestamp: string;
}

export interface HotelStats {
  success: boolean;
  data: {
    total_hotels: number;
    active_hotels: number;
    inactive_hotels: number;
    maintenance_hotels: number;
    avg_rating: number;
  };
  timestamp: string;
}

export interface HotelAmenities {
  id?: number;
  hotel_id: number;
  free_wifi: boolean;
  parking_available: boolean;
  parking_type?: 'Free' | 'Paid' | null;
  restaurant: boolean;
  bar_lounge: boolean;
  fitness_center: boolean;
  swimming_pool: boolean;
  spa_wellness: boolean;
  elevator: boolean;
  airport_shuttle: boolean;
  front_desk_24x7: boolean;
  business_center: boolean;
  pet_friendly: boolean;
  non_smoking_property: boolean;
  air_conditioning: boolean;
  private_bathroom: boolean;
  flat_screen_tv: boolean;
  work_desk: boolean;
  minibar: boolean;
  coffee_tea_maker: boolean;
  hairdryer: boolean;
  wheelchair_access: boolean;
  braille_signage: boolean;
  accessible_bathrooms: boolean;
  created_at?: string;
  updated_at?: string;
}

class HotelService {
  // Get all hotels with pagination and filters
  async getHotels(page: number = 1, limit: number = 10, filters: HotelFilters = {}): Promise<HotelResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add filter parameters (only if they have values)
    if (filters.status) params.append('status', filters.status);
    if (filters.city) params.append('city', filters.city);
    if (filters.country) params.append('country', filters.country);
    if (filters.hotel_type) params.append('hotel_type', filters.hotel_type);
    if (filters.extranet_hotel_id) params.append('extranet_hotel_id', filters.extranet_hotel_id);
    if (filters.star_rating) params.append('star_rating', filters.star_rating.toString());
    if (filters.search) params.append('search', filters.search);

    const response = await api.get(`/hotels?${params}`);
    return response.data;
  }

  // Get hotel by ID
  async getHotelById(id: number): Promise<{ success: boolean; data: Hotel; timestamp: string }> {
    const response = await api.get(`/hotels/${id}`);
    return response.data;
  }

  // Get hotel statistics
  async getHotelStats(): Promise<HotelStats> {
    const response = await api.get('/hotels/stats');
    return response.data;
  }

  // Create new hotel
  async createHotel(hotelData: any): Promise<{ success: boolean; message: string; data: Hotel; timestamp: string }> {
    const response = await api.post('/hotels', hotelData);
    return response.data;
  }

  // Update hotel
  async updateHotel(id: number, hotelData: any): Promise<{ success: boolean; message: string; data: Hotel; timestamp: string }> {
    const response = await api.put(`/hotels/${id}`, hotelData);
    return response.data;
  }

  // Delete hotel (soft delete)
  async deleteHotel(id: number): Promise<{ success: boolean; message: string; timestamp: string }> {
    const response = await api.delete(`/hotels/${id}`);
    return response.data;
  }

  // Toggle hotel status
  async toggleHotelStatus(id: number, status: 'active' | 'inactive' | 'suspended'): Promise<{ success: boolean; message: string; timestamp: string }> {
    const response = await api.put(`/hotels/${id}/status`, { status });
    return response.data;
  }

  // Get hotel amenities
  async getHotelAmenities(id: number): Promise<{ success: boolean; data: HotelAmenities | null; timestamp: string }> {
    const response = await api.get(`/hotels/${id}/amenities`);
    return response.data;
  }

  // Update hotel amenities
  async updateHotelAmenities(id: number, amenities: Partial<HotelAmenities>): Promise<{ success: boolean; message: string; data: HotelAmenities; timestamp: string }> {
    const response = await api.put(`/hotels/${id}/amenities`, amenities);
    return response.data;
  }
}

export default new HotelService();
